import { forwardRef, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { FeedbackAction } from './InlineAlert';
import type { Action, WithClassName } from './types';

export type SectionProps = WithClassName & {
  id?: string;
  title?: string;
  description?: string;
  children: ReactNode;
  actions?: readonly Action[];
  headingLevel?: 2 | 3;
  collapsible?: boolean;
  expanded?: boolean;
  defaultExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
};

export const Section = forwardRef<HTMLElement, SectionProps>(function Section({
  id, title, description, children, actions, headingLevel = 2, collapsible = false,
  expanded, defaultExpanded = true, onExpandedChange, className,
}, ref) {
  const generatedId = useId();
  const sectionId = id ?? `ui-section-${generatedId}`;
  const titleId = `${sectionId}-title`;
  const contentId = `${sectionId}-content`;
  const [localExpanded, setLocalExpanded] = useState(defaultExpanded);
  const open = !collapsible || (expanded ?? localExpanded);
  const content = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);
  const focusedWithin = useRef(false);
  const heading = title ?? (collapsible ? 'Section' : undefined);
  const Heading = headingLevel === 3 ? 'h3' : 'h2';

  useLayoutEffect(() => {
    if (!open && (focusedWithin.current || content.current?.contains(document.activeElement))) {
      toggle.current?.focus();
      focusedWithin.current = false;
    }
  }, [open]);

  function changeExpanded() {
    if (expanded === undefined) setLocalExpanded(!open);
    onExpandedChange?.(!open);
  }

  return <section ref={ref} id={sectionId} aria-labelledby={heading ? titleId : undefined}
    className={`ui-section ${className ?? ''}`}>
    {(heading || description || !!actions?.length) && <div className="ui-section__header">
      <div className="ui-section__context">
        {heading && <Heading id={titleId} className="ui-section__title">
          {collapsible ? <button ref={toggle} type="button" className="ui-section__toggle"
            aria-expanded={open} aria-controls={contentId} onClick={changeExpanded}>
            <span aria-hidden="true">{open ? '−' : '+'}</span>{heading}
          </button> : heading}
        </Heading>}
        {description && <p className="ui-section__description">{description}</p>}
      </div>
      {!!actions?.length && <div className="ui-structure-actions">
        {actions.map((action) => <FeedbackAction key={action.id} action={action} />)}
      </div>}
    </div>}
    <div ref={content} id={contentId} className="ui-section__content" hidden={!open}
      onFocusCapture={() => { focusedWithin.current = true; }}
      onBlurCapture={(event) => {
        if (event.relatedTarget) focusedWithin.current = event.currentTarget.contains(event.relatedTarget as Node);
      }}>{children}</div>
  </section>;
});
